import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import PageTransition from '../components/PageTransition'
import CompactGroupCard from '../components/CompactGroupCard'
import QuickActions from '../components/QuickActions'
import { EmptyGroups } from '../components/EmptyStates'
import PullToRefresh from '../components/PullToRefresh'
import SafeIcon from '../common/SafeIcon'
import { FiArrowRight, FiZap } from 'react-icons/fi'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import DashboardSkeleton from '../components/skeletons/DashboardSkeleton'

/**
 * Pick the most relevant next action for the user based on what they've set up so far:
 *   1. No availability set       → encourage them to set availability
 *   2. Availability but no group → encourage them to create or join a group
 *   3. Both done                 → schedule a round (the original CTA)
 */
const getSuggestedAction = ({ hasAvailability, hasGroup }) => {
  if (!hasAvailability) {
    return {
      title: 'Set your availability',
      subtitle: 'Add the times you’re free to play',
      to: '/my-availability',
    }
  }
  if (!hasGroup) {
    return {
      title: 'Create or join a group',
      subtitle: 'Connect with players to start scheduling',
      to: '/groups',
    }
  }
  return {
    title: 'Schedule a Round',
    subtitle: 'Plan your next tee time',
    to: '/create-round',
  }
}

function Dashboard({ groups, loading, onJoinSuccess, onRefresh }) {
  const { user } = useAuth()
  const totalMembers = groups.reduce((sum, group) => sum + group.members.length, 0)

  // null while loading, true/false once known. While null we hide the CTA
  // to avoid flashing one suggestion then swapping to another.
  const [hasAvailability, setHasAvailability] = useState(null)

  useEffect(() => {
    if (!user) return
    let cancelled = false
    ;(async () => {
      const { count, error } = await supabase
        .from('user_availability')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
      if (cancelled) return
      if (error) {
        // On error, assume they've set availability so we don't nag them.
        setHasAvailability(true)
        return
      }
      setHasAvailability((count ?? 0) > 0)
    })()
    return () => {
      cancelled = true
    }
  }, [user])

  const suggested = hasAvailability === null
    ? null
    : getSuggestedAction({ hasAvailability, hasGroup: groups.length > 0 })

  if (loading) {
    return (
      <>
        <SEO title="Dashboard | TeeSync" noindex={true} />
        <DashboardSkeleton />
      </>
    )
  }

  return (
    <PullToRefresh onRefresh={onRefresh} disabled={loading || !onRefresh}>
      <SEO title="Dashboard | TeeSync" noindex={true} />
      <div className="min-h-screen bg-app">
        <PageTransition className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Suggested next action - content adapts to what the user has set up.
            Hidden until we know the user's availability state to avoid flicker. */}
        {suggested && (
          <div>
            <Link to={suggested.to}>
              <div className="bg-accent rounded-lg p-5 cursor-pointer hover:bg-interactive-hover transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-card/20 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiZap} className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="block text-[10px] font-semibold uppercase tracking-wider text-white/70 mb-0.5">
                        Suggested
                      </span>
                      <h3 className="text-base font-medium text-white">{suggested.title}</h3>
                      <p className="text-white/70 text-sm">{suggested.subtitle}</p>
                    </div>
                  </div>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5 text-white" />
                </div>
              </div>
            </Link>
          </div>
        )}

        {/* Quick Actions - flattened: action tiles sit directly on the page
            background instead of nesting inside an outer card. */}
        <div>
          <h2 className="text-base font-medium text-text-primary mb-4">Quick Actions</h2>
          <QuickActions onJoinSuccess={onJoinSuccess} groups={groups} />
        </div>

        {/* Groups Section */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-text-primary">Your Groups</h2>
              <p className="text-text-muted text-sm mt-1">
                {groups.length} group{groups.length !== 1 ? 's' : ''} · {totalMembers} member{totalMembers !== 1 ? 's' : ''}
              </p>
            </div>
            <Link
              to="/groups"
              className="text-accent hover:text-interactive-hover text-sm font-medium flex items-center gap-1 transition-colors"
            >
              View all
              <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
            </Link>
          </div>

          {groups.length > 0 ? (
            /* Mobile: vertical stack, full-width cards.
               Desktop: 2-3 column grid. */
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
              {groups.map((group) => (
                <CompactGroupCard key={group.id} group={group} />
              ))}
            </div>
          ) : (
            <div className="bg-card rounded-lg border border-border-subtle py-8">
              <EmptyGroups onJoinGroup={() => onJoinSuccess()} />
            </div>
          )}
        </div>

        {/* Bottom Spacing for Mobile Nav */}
        <div className="h-20 lg:h-0" />
        </PageTransition>
      </div>
    </PullToRefresh>
  )
}

export default Dashboard
